import { useEffect, useState } from "react";
import { Download, FileBarChart } from "lucide-react";
import { toast } from "sonner";
import api, { API, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function ReportsAdmin() {
  const [games, setGames] = useState([]);
  const [locs, setLocs] = useState([]);
  const [f, setF] = useState({ game_id: "all", location_id: "all", date_from: "", date_to: "" });
  const [preview, setPreview] = useState([]);

  useEffect(() => {
    api.get("/admin/games").then((r) => setGames(r.data));
    api.get("/admin/locations").then((r) => setLocs(r.data));
  }, []);

  const params = () => {
    const p = {};
    if (f.game_id !== "all") p.game_id = f.game_id;
    if (f.location_id !== "all") p.location_id = f.location_id;
    if (f.date_from) p.date_from = f.date_from;
    if (f.date_to) p.date_to = f.date_to;
    return p;
  };

  const run = () => api.get("/admin/bookings", { params: params() }).then((r) => setPreview(r.data));
  useEffect(() => { run(); }, []);

  const download = async () => {
    try {
      const res = await api.get("/admin/reports/export", { params: params(), responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = "escaperoom_report.csv"; a.click();
      URL.revokeObjectURL(url);
      toast.success("Report downloaded");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const revenue = preview.filter((b) => b.payment_status === "paid").reduce((a, b) => a + b.total_amount, 0);

  return (
    <div>
      <h1 className="font-display font-extrabold text-3xl mb-1">Reports</h1>
      <p className="text-slate-400 mb-6">Filter by date, location & game — then export to CSV.</p>
      <div className="rounded-2xl border border-white/10 bg-[#131a29] p-5 mb-6">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <Select value={f.game_id} onValueChange={(v) => setF({ ...f, game_id: v })}>
            <SelectTrigger data-testid="report-game" className="bg-slate-950/50 border-white/10"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">All games</SelectItem>{games.map((g) => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={f.location_id} onValueChange={(v) => setF({ ...f, location_id: v })}>
            <SelectTrigger data-testid="report-loc" className="bg-slate-950/50 border-white/10"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">All locations</SelectItem>{locs.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}</SelectContent>
          </Select>
          <Input type="date" value={f.date_from} onChange={(e) => setF({ ...f, date_from: e.target.value })} data-testid="report-from" className="bg-slate-950/50 border-white/10" />
          <Input type="date" value={f.date_to} onChange={(e) => setF({ ...f, date_to: e.target.value })} data-testid="report-to" className="bg-slate-950/50 border-white/10" />
        </div>
        <div className="flex gap-3 mt-4">
          <Button onClick={run} data-testid="run-report" variant="secondary"><FileBarChart className="h-4 w-4 mr-1" /> Run</Button>
          <Button onClick={download} data-testid="export-report" className="bg-violet-600 hover:bg-violet-500"><Download className="h-4 w-4 mr-1" /> Export CSV</Button>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-6">
        <div className="rounded-xl border border-white/10 bg-[#131a29] p-4"><div className="text-2xl font-display font-extrabold">{preview.length}</div><div className="text-xs text-slate-400">Bookings</div></div>
        <div className="rounded-xl border border-white/10 bg-[#131a29] p-4"><div className="text-2xl font-display font-extrabold text-emerald-400">₹{revenue}</div><div className="text-xs text-slate-400">Paid revenue</div></div>
        <div className="rounded-xl border border-white/10 bg-[#131a29] p-4"><div className="text-2xl font-display font-extrabold text-amber-400">{preview.reduce((a, b) => a + b.num_players, 0)}</div><div className="text-xs text-slate-400">Players</div></div>
      </div>

      <div className="rounded-2xl border border-white/10 bg-[#131a29] overflow-x-auto">
        <table className="w-full text-sm min-w-[700px]">
          <thead className="bg-slate-950/50 text-slate-400 text-xs uppercase"><tr>{["Ref", "Game", "Location", "Date", "Players", "Total", "Status"].map((h) => <th key={h} className="text-left px-3 py-3">{h}</th>)}</tr></thead>
          <tbody>
            {preview.map((b) => (
              <tr key={b.id} className="border-t border-white/5">
                <td className="px-3 py-2 font-mono text-violet-300">{b.booking_ref}</td>
                <td className="px-3 py-2">{b.game_name}</td>
                <td className="px-3 py-2 text-slate-400">{b.location_name}</td>
                <td className="px-3 py-2 text-slate-400">{b.date}</td>
                <td className="px-3 py-2">{b.num_players}</td>
                <td className="px-3 py-2 font-mono">₹{b.total_amount}</td>
                <td className="px-3 py-2">{b.payment_status}</td>
              </tr>
            ))}
            {preview.length === 0 && <tr><td colSpan={7} className="px-3 py-10 text-center text-slate-500">No data for this filter.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
